import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../../utils/authUtils';
import { clearCart, getCart } from '../../utils/cartStore';
import AddressFields from '../../components/shop/AddressFields';
import { formatVnd } from '../../constants/shopOrderStatus';
import { quoteShipping, WAREHOUSE } from '../../utils/shippingFee';
import { resolveImageSrc } from '../../utils/productGallery';
import { PLACEHOLDER_300 } from '../../utils/imagePlaceholder';
import './CheckoutPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(getCart());
  const [form, setForm] = useState({ receiverName: '', receiverPhone: '', note: '' });
  const [shipping, setShipping] = useState({ address: '', complete: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setItems(getCart());
  }, []);

  const goodsTotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.price) * Number(item.quantity), 0),
    [items]
  );
  const shippingQuote = quoteShipping(goodsTotal, shipping.provinceName);
  const payable = goodsTotal;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!items.length) {
      setError('Giỏ hàng đang trống');
      return;
    }
    if (!shipping.complete) {
      setError('Chọn đủ tỉnh/thành, phường/xã và số nhà');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/shop/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          receiverAddress: shipping.address,
          provinceName: shipping.provinceName,
          items: items.map((item) => ({ productId: item.productId, quantity: item.quantity })),
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.message || 'Không đặt được hàng');
        return;
      }
      clearCart();
      navigate('/shop/orders', { state: { createdCode: data.data?.order_code } });
    } catch (_) {
      setError('Không kết nối được máy chủ');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-card">
        <button type="button" className="checkout-back" onClick={() => navigate('/shop/products')}>
          Quay lại cửa hàng
        </button>
        <h1>Đặt hàng</h1>
        {!items.length ? (
          <p className="checkout-empty">Giỏ hàng trống. Hãy chọn sản phẩm trước khi đặt.</p>
        ) : (
          <div className="checkout-layout">
            <div className="checkout-items">
              {items.map((item) => (
                <div key={item.productId} className="checkout-item">
                  <img
                    src={item.imageUrl ? resolveImageSrc(item.imageUrl) : PLACEHOLDER_300}
                    alt=""
                    onError={(e) => { e.target.src = PLACEHOLDER_300; }}
                  />
                  <div>
                    <div className="checkout-item-name">{item.name}</div>
                    <div className="checkout-item-price">₫{formatVnd(item.price)}</div>
                  </div>
                  <span>× {item.quantity}</span>
                </div>
              ))}
              <div className="checkout-total">
                <div>Tiền hàng: ₫{formatVnd(goodsTotal)}</div>
                <div>Phí ship: Miễn phí</div>
                <div className="ship-note">{shippingQuote.label}</div>
                <div className="ship-note">Kho gửi: {WAREHOUSE.name}</div>
                <div>Tổng thanh toán: ₫{formatVnd(payable)}</div>
              </div>
            </div>
            <form className="checkout-form" onSubmit={submit}>
              <label>
                Người nhận
                <input value={form.receiverName} onChange={(e) => setForm({ ...form, receiverName: e.target.value })} required />
              </label>
              <label>
                Số điện thoại
                <input value={form.receiverPhone} onChange={(e) => setForm({ ...form, receiverPhone: e.target.value })} required />
              </label>
              <AddressFields value={shipping} onChange={setShipping} />
              <label>
                Ghi chú
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </label>
              {error ? <p className="checkout-error">{error}</p> : null}
              <button type="submit" className="checkout-submit" disabled={submitting}>
                {submitting ? 'Đang gửi đơn...' : 'Xác nhận đặt hàng'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
